import type { ChartV2Horizon, ChartV2Locales } from '../types/chart-v2.types';
import { Chip } from './Chip';
import styles from './HorizonSelector.module.scss';

/**
 * Props for `<HorizonSelector>`.
 *
 * Required: `horizons`, `selected`, `onChange`. Renders one chip per
 * horizon and reports clicks via `onChange`.
 */
export interface HorizonSelectorProps {
  /** Horizons to render as chips, in display order. */
  horizons: ChartV2Horizon[];
  /** Active horizon, or `null` when the window is a custom pan/zoom. */
  selected: ChartV2Horizon | null;
  /** Disables every chip in the group. */
  disabled?: boolean;
  /** Subset of `horizons` to render in a disabled state. */
  disabledHorizons?: ChartV2Horizon[];
  /** Called with the picked horizon when the user clicks a chip. */
  onChange: (horizon: ChartV2Horizon) => void;
  /**
   * Per-horizon label overrides (e.g. localised strings). Keys not
   * present fall back to the horizon code itself (`"1D"`, `"3M"`, …).
   * `ChartV2Locales` keys may also be supplied for downstream
   * components.
   */
  locales?: Partial<Record<ChartV2Horizon, string>> & ChartV2Locales;
}

/**
 * Chip-bar that lets the user pick a time horizon (`"1D"`, `"3M"`,
 * `"1Y"`, etc.) for a chart.
 *
 * Renders one uppercase chip per horizon. The matching chip is
 * highlighted; chips listed in `disabledHorizons` are greyed out
 * (e.g. when a backend reports no data for that range).
 */
export const HorizonSelector = ({
  horizons,
  selected,
  disabled,
  disabledHorizons = [],
  onChange,
  locales,
}: HorizonSelectorProps) => (
  <div className={styles.root} data-testid="chartv2-horizon-selector">
    {horizons.map(h => (
      <Chip
        key={h}
        label={locales?.[h] ?? h}
        uppercase
        isActive={h === selected}
        disabled={disabled || disabledHorizons.includes(h)}
        onClick={() => onChange(h)}
        data-testid={`chartv2-horizon-${h}`}
      />
    ))}
  </div>
);
